import React, { useState, useEffect, useMemo } from 'react';
import { getData, getUserProfile } from '../firebase';
import { invoiceSettlement } from '../ledgers';
import { exportToCSV, exportToPDF } from '../exportUtils';

const BRAND = '#0f6e56';
const today = () => new Date().toISOString().slice(0, 10);

export default function DailySales({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [business, setBusiness] = useState(null);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [inv, rec, pay] = await Promise.all([getData(user.uid, 'invoices'), getData(user.uid, 'receipts'), getData(user.uid, 'payments')]);
    setInvoices(inv); setReceipts(rec); setPayments(pay);
    getUserProfile(user.uid).then(setBusiness);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const cur = (business && business.currency) ? business.currency + ' ' : '$';
  const fmt = n => cur + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const sales = useMemo(() => invoices
    .filter(i => i.type === 'sales' && i.date >= from && i.date <= to)
    .sort((a, b) => (a.date || '').localeCompare(b.date || '')), [invoices, from, to]);

  const totals = useMemo(() => {
    let fare = 0, disc = 0, tax = 0, net = 0, collected = 0, outstanding = 0;
    sales.forEach(i => {
      fare += Number(i.fareAmount != null ? i.fareAmount : i.amount) || 0;
      disc += Number(i.discountAmount) || 0;
      tax += Number(i.taxAmount) || 0;
      net += Number(i.amount) || 0;
      const s = invoiceSettlement(i, receipts, payments);
      collected += s.paid; outstanding += s.outstanding;
    });
    return { fare, disc, tax, net, collected, outstanding, count: sales.length };
  }, [sales, receipts, payments]);

  // group by day for the daily summary
  const byDay = useMemo(() => {
    const m = {};
    sales.forEach(i => {
      const d = i.date || '—';
      if (!m[d]) m[d] = { date: d, count: 0, net: 0, tax: 0 };
      m[d].count++; m[d].net += Number(i.amount) || 0; m[d].tax += Number(i.taxAmount) || 0;
    });
    return Object.values(m).sort((a, b) => a.date.localeCompare(b.date));
  }, [sales]);

  const quick = (kind) => {
    const t = today(); const d = new Date();
    if (kind === 'today') { setFrom(t); setTo(t); }
    if (kind === 'week') { const w = new Date(d); w.setDate(d.getDate() - 6); setFrom(w.toISOString().slice(0, 10)); setTo(t); }
    if (kind === 'month') { setFrom(t.slice(0, 8) + '01'); setTo(t); }
  };

  const csv = () => {
    const headers = ['Date', 'Invoice', 'Customer', 'Passenger', 'PNR', 'Sector', 'Airline', 'Ticket', 'Fare', 'Discount', 'Tax', 'Net', 'Status'];
    const rows = sales.map(i => {
      const s = invoiceSettlement(i, receipts, payments);
      return [i.date, i.invoiceNo, i.customerName, i.paxName || '', i.pnr || '', i.sector || '', i.airline || '', i.ticketNo || '',
        (Number(i.fareAmount != null ? i.fareAmount : i.amount) || 0).toFixed(2), (Number(i.discountAmount) || 0).toFixed(2),
        (Number(i.taxAmount) || 0).toFixed(2), (Number(i.amount) || 0).toFixed(2), s.status];
    });
    rows.push(['', '', '', '', '', '', '', 'TOTAL', totals.fare.toFixed(2), totals.disc.toFixed(2), totals.tax.toFixed(2), totals.net.toFixed(2), '']);
    exportToCSV(`daily-sales_${from}_to_${to}.csv`, headers, rows);
  };

  const pdf = () => {
    const body = `
      <div class="section-title">Summary (${from} to ${to})</div>
      <table><tbody>
        <tr><td>Invoices</td><td class="num">${totals.count}</td></tr>
        <tr><td>Gross Fare</td><td class="num">${fmt(totals.fare)}</td></tr>
        <tr><td>Discount</td><td class="num red">− ${fmt(totals.disc)}</td></tr>
        <tr><td>Tax</td><td class="num">${fmt(totals.tax)}</td></tr>
        <tr class="total"><td>Net Sales</td><td class="num green">${fmt(totals.net)}</td></tr>
        <tr><td>Collected</td><td class="num green">${fmt(totals.collected)}</td></tr>
        <tr><td>Outstanding</td><td class="num red">${fmt(totals.outstanding)}</td></tr>
      </tbody></table>
      <div class="section-title">Sales Register</div>
      <table>
        <thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th>PNR/Sector</th><th class="num">Net</th><th>Status</th></tr></thead>
        <tbody>
          ${sales.map(i => { const s = invoiceSettlement(i, receipts, payments); return `<tr><td>${i.date}</td><td>${i.invoiceNo}</td><td>${i.customerName || ''}${i.paxName ? ' / ' + i.paxName : ''}</td><td>${[i.pnr, i.sector].filter(Boolean).join(' · ')}</td><td class="num">${fmt(i.amount)}</td><td>${s.status}</td></tr>`; }).join('')}
        </tbody>
        <tfoot><tr class="total"><td colspan="4">Total Net Sales</td><td class="num green">${fmt(totals.net)}</td></tr></tfoot>
      </table>`;
    exportToPDF('Daily Sales Report', business?.businessName, body, `${from} to ${to}`);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Daily Sales Report</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={csv} style={ghost}>⬇ Excel (CSV)</button>
          <button onClick={pdf} style={btn}>🖨️ PDF</button>
        </div>
      </div>
      <div style={{ color: '#888', fontSize: 14, marginBottom: 18 }}>Sales data and totals update automatically as invoices are added.</div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: 16, marginBottom: 16, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div><label style={lbl}>From</label><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} /></div>
        <div><label style={lbl}>To</label><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} /></div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['today', 'Today'], ['week', 'Last 7 days'], ['month', 'This month']].map(([k, l]) => (
            <button key={k} onClick={() => quick(k)} style={chip}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 18 }}>
        <Stat label="Invoices" value={totals.count} color="#555" />
        <Stat label="Net Sales" value={fmt(totals.net)} color={BRAND} />
        <Stat label="Tax Collected" value={fmt(totals.tax)} color="#185fa5" />
        <Stat label="Collected" value={fmt(totals.collected)} color={BRAND} />
        <Stat label="Outstanding" value={fmt(totals.outstanding)} color="#854f0b" />
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>Loading...</div> : (
        <>
          {byDay.length > 1 && (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Day-by-day</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr><Th>Date</Th><Th right>Invoices</Th><Th right>Tax</Th><Th right>Net Sales</Th></tr></thead>
                <tbody>{byDay.map(d => <tr key={d.date}><Td>{d.date}</Td><Td right>{d.count}</Td><Td right>{fmt(d.tax)}</Td><Td right>{fmt(d.net)}</Td></tr>)}</tbody>
              </table>
            </div>
          )}

          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Sales Register</div>
            {sales.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>No sales in this period.</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr><Th>Date</Th><Th>Invoice</Th><Th>Customer / Pax</Th><Th>PNR · Sector</Th><Th right>Net</Th><Th>Status</Th></tr></thead>
                  <tbody>
                    {sales.map(i => {
                      const s = invoiceSettlement(i, receipts, payments);
                      const c = { paid: BRAND, partial: '#185fa5', unpaid: '#854f0b' }[s.status];
                      return (
                        <tr key={i.id}>
                          <Td>{i.date}</Td>
                          <Td>{i.invoiceNo}</Td>
                          <Td>{i.customerName}{i.paxName ? <span style={{ color: '#888' }}> / {i.paxName}</span> : ''}</Td>
                          <Td>{[i.pnr, i.sector].filter(Boolean).join(' · ')}</Td>
                          <Td right><b>{fmt(i.amount)}</b></Td>
                          <Td><span style={{ color: c, fontWeight: 600, textTransform: 'capitalize' }}>{s.status}</span></Td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr style={{ background: '#f7f8fa' }}><Td><b>Total</b></Td><Td></Td><Td></Td><Td></Td><Td right><b style={{ color: BRAND }}>{fmt(totals.net)}</b></Td><Td></Td></tr></tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: '14px 18px' }}>
      <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}
const Th = ({ children, right }) => <th style={{ textAlign: right ? 'right' : 'left', padding: '8px 12px', borderBottom: '2px solid #eee', fontSize: 11, color: '#888', textTransform: 'uppercase' }}>{children}</th>;
const Td = ({ children, right }) => <td style={{ textAlign: right ? 'right' : 'left', padding: '8px 12px', borderBottom: '1px solid #f2f2f2' }}>{children}</td>;

const btn = { padding: '9px 18px', background: BRAND, color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const ghost = { padding: '9px 18px', background: '#fff', color: BRAND, border: `1px solid ${BRAND}`, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const chip = { padding: '8px 12px', background: '#f2f2f2', color: '#555', border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: 12, color: '#888', marginBottom: 4 };
const inp = { padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, outline: 'none' };
